/**
 * Shared-компонент «Документы сотрудников заказа обучения» (centrika-docs!59,
 * `component.training-order.employee-documents`; ранее «Согласия на обработку персональных
 * данных», centrika-docs!40): карточка со счётчиками («X из Y» — согласия и документы
 * об образовании), шаблоном согласия и единой таблицей сотрудников заказа со столбцами
 * «Согласие ПД» и документа об образовании. Документ — только у слушателей программ с базой
 * «ВО/СПО» (training-listener-completeness, ответ заказчицы В12): нет таких — нет ни столбца, ни
 * счётчика.
 *
 * Показывается на экране «Заказ отправлен» формы заказа обучения и на странице заказа клиента;
 * состояние согласий и документов в обоих одно и то же, поэтому компонент данные не грузит:
 * состав сотрудников и колбэк обновления даёт вызывающая сторона.
 *
 * Согласие и документ об образовании доступны независимо от статуса заказа — их отсутствие не
 * меняет статус заказа и не мешает менеджеру. Оба живут в карточке сотрудника (один файл — PDF
 * или фото JPG/PNG — на человека, во всех заказах): согласие, уже загруженное в карточку,
 * подтягивается в новый заказ без повторной загрузки, а загруженное из заказа заменяет прежнее в
 * карточке. Клиенту доступны
 * загрузка/замена/скачивание/удаление; сотруднику Центрики — только просмотр/скачивание
 * (`readOnly`).
 */
import { useState } from 'react'
import { Download, Users } from 'lucide-react'
import {
  companyApi,
  trainingApi,
  type CompanyScopeApi,
  type EducationDocumentStatus,
  type TrainingSelectedEmployee,
} from '@/lib/api'
import { downloadFromUrl } from '@/lib/download'
import {
  EDUCATION_DOCUMENT_LABEL,
  TRAINING_PERSON_DOC_ACCEPT,
  TRAINING_PERSON_DOC_FORMATS,
  trainingConsentCounterLabel,
  trainingEducationDocCounterLabel,
} from '@/lib/training'
import { isValidEmail } from '@/lib/validation'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { SingleDocumentUpload } from '@/design-system/components/data/single-document-upload'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

/**
 * Минимум, который ячейке согласия нужен от строки: сотрудник и состояние его файла. Строка
 * шага 3 (`TrainingSelectedEmployee`), строка слушателя состава (`TrainingTrainee`) и согласие
 * карточки сотрудника подходят — ячейка возвращает обновлённый объект того же типа.
 */
export interface ConsentCellEmployee {
  personId: string
  hasConsent: boolean
  consentFileName?: string
  consentUrl?: string
}

/**
 * Поверхность API согласия — клиентская (`companyApi`) по умолчанию (состав заказа/шаг 3, всегда
 * клиент) либо явный scoped api из карточки сотрудника.
 */
export type ConsentApi = Pick<CompanyScopeApi, 'uploadConsent' | 'deleteConsent'>

/**
 * Ячейка «Согласие ПД» одного сотрудника: статус, ссылка на файл и действия загрузки/замены/
 * удаления. Согласие привязано к КАРТОЧКЕ СОТРУДНИКА (api по personId), поэтому его состояние
 * одинаково во всех строках человека и во всех заказах. `readOnly` (сотрудник Центрики,
 * centrika-docs!59) прячет действия — остаётся статус и ссылка на просмотр/скачивание.
 *
 * Экспортируется: та же ячейка стоит в строке слушателя состава заказа обучения (centrika-docs!41)
 * и в карточке сотрудника.
 */
export function ConsentCell<T extends ConsentCellEmployee>({
  employee,
  onChanged,
  readOnly,
  api = companyApi,
  compact,
  documentLabel,
}: {
  employee: T
  onChanged: (employee: T) => void
  readOnly?: boolean
  /** Компактно — для строки таблицы (кнопки-значки, подпись в подсказке). */
  compact?: boolean
  /** Подпись документа в подсказках компактных кнопок; по умолчанию «Согласие ПД». */
  documentLabel?: string
  /** Scoped-поверхность API (по умолчанию клиентская `companyApi`; карточка передаёт свою). */
  api?: ConsentApi
}) {
  async function upload(file: File) {
    const status = await api.uploadConsent(employee.personId, file)
    onChanged({
      ...employee,
      hasConsent: status.hasConsent,
      consentFileName: status.consentFileName,
      consentUrl: status.consentUrl,
    })
  }

  async function remove() {
    const status = await api.deleteConsent(employee.personId)
    onChanged({
      ...employee,
      hasConsent: status.hasConsent,
      consentFileName: status.consentFileName,
      consentUrl: status.consentUrl,
    })
  }

  return (
    <SingleDocumentUpload
      file={
        employee.hasConsent && employee.consentUrl
          ? { name: employee.consentFileName || 'Скачать файл', downloadUrl: employee.consentUrl }
          : null
      }
      accept={TRAINING_PERSON_DOC_ACCEPT}
      maxSizeBytes={15 * 1024 * 1024}
      disabled={readOnly}
      inputAriaLabel="Согласие ПД"
      onUpload={upload}
      onDelete={remove}
      compact={compact}
      documentLabel={documentLabel ?? 'Согласие ПД'}
    />
  )
}

/**
 * Минимум для ячейки документа об образовании: сотрудник и состояние его документа. Строка
 * шага 3 и строка слушателя состава обе подходят — ячейка возвращает объект того же типа с
 * обновлённым `educationDocument`.
 */
export interface EducationDocCellEmployee {
  personId: string
  educationDocument?: EducationDocumentStatus
}

/**
 * Поверхность API документа об образовании — клиентская (`companyApi`) по умолчанию (состав
 * заказа/шаг 3, всегда клиент) либо явный scoped api из карточки сотрудника.
 */
export type EducationDocumentApi = Pick<
  CompanyScopeApi,
  'uploadEducationDocument' | 'deleteEducationDocument'
>

/**
 * Ячейка «Образование» (документ об образовании, centrika-docs!59): статус, ссылка на файл и
 * действия загрузки/замены/удаления. Документ привязан к КАРТОЧКЕ СОТРУДНИКА (companyApi по
 * personId), поэтому его состояние одинаково во всех строках человека и во всех заказах.
 * `readOnly` (сотрудник Центрики) прячет действия — остаётся статус и ссылка.
 */
export function EducationDocumentCell<T extends EducationDocCellEmployee>({
  employee,
  onChanged,
  readOnly,
  api = companyApi,
  compact,
  documentLabel,
}: {
  employee: T
  onChanged: (employee: T) => void
  readOnly?: boolean
  /** Компактно — для строки таблицы (кнопки-значки, подпись в подсказке). */
  compact?: boolean
  /** Подпись документа в подсказках компактных кнопок; по умолчанию «Документ об образовании». */
  documentLabel?: string
  /** Scoped-поверхность API (по умолчанию клиентская `companyApi`; карточка передаёт свою). */
  api?: EducationDocumentApi
}) {
  const doc = employee.educationDocument
  const hasDocument = Boolean(doc?.hasDocument)

  async function upload(file: File) {
    const status = await api.uploadEducationDocument(employee.personId, file)
    onChanged({ ...employee, educationDocument: status })
  }

  async function remove() {
    const status = await api.deleteEducationDocument(employee.personId)
    onChanged({ ...employee, educationDocument: status })
  }

  return (
    <SingleDocumentUpload
      file={
        hasDocument && doc?.url
          ? { name: doc.fileName || 'Скачать файл', downloadUrl: doc.url }
          : null
      }
      accept={TRAINING_PERSON_DOC_ACCEPT}
      maxSizeBytes={15 * 1024 * 1024}
      disabled={readOnly}
      inputAriaLabel="Документ об образовании"
      onUpload={upload}
      onDelete={remove}
      compact={compact}
      documentLabel={documentLabel ?? 'Документ об образовании'}
    />
  )
}

export interface EmployeeDocumentsProps {
  /**
   * Уникальные сотрудники заказа на момент отправки (сервер отдаёт по одной строке на
   * person_id) — счётчики и таблица считают человека один раз независимо от числа программ.
   */
  employees: TrainingSelectedEmployee[]
  /** Физлицо (individual-training-order): документы — его собственные. */
  individual?: boolean
  /** Вызывается после загрузки/замены/удаления согласия или документа — обновляет строку. */
  onEmployeeChanged: (employee: TrainingSelectedEmployee) => void
}

/**
 * Карточка «Документы сотрудников» — e-mail, согласия ПД и документы об образовании сотрудников
 * отправленного заказа обучения (клиент управляет всем).
 */
export function EmployeeDocuments({
  employees,
  onEmployeeChanged,
  individual = false,
}: EmployeeDocumentsProps) {
  const consentsUploaded = employees.filter((e) => e.hasConsent).length
  // Документ об образовании — только у слушателей программ с базой «ВО/СПО» (ответ заказчицы В12:
  // где документ не нужен, поля быть не должно).
  const needDocs = employees.filter((e) => e.needsEducationDocument)
  const docsUploaded = needDocs.filter((e) => e.educationDocument?.hasDocument).length

  // Черновики e-mail по personId (centrika-docs!87): ввод НЕ сохраняется автоматически —
  // значение уходит в карточку сотрудника только по кнопке «Сохранить». Пока черновика нет,
  // поле показывает актуальный e-mail карточки.
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({})
  const [savingEmail, setSavingEmail] = useState(false)
  // Сотрудники, чей e-mail не удалось сохранить в последней попытке: показываем сообщение и
  // держим кнопку доступной для повтора; успешные значения при этом остаются в карточках.
  const [emailFailed, setEmailFailed] = useState<Record<string, boolean>>({})

  const emailValue = (e: TrainingSelectedEmployee) => emailDrafts[e.personId] ?? e.email ?? ''
  const emailChanged = (e: TrainingSelectedEmployee) =>
    emailValue(e).trim() !== (e.email ?? '').trim()
  const emailFilledValid = (value: string) => value.trim() === '' || isValidEmail(value.trim())

  const changedEmployees = employees.filter(emailChanged)
  const allEmailsValid = employees.every((e) => emailFilledValid(emailValue(e)))
  // Кнопка доступна, если изменён хотя бы один e-mail и все заполненные поля корректны.
  const canSaveEmails = !savingEmail && changedEmployees.length > 0 && allEmailsValid

  function setEmailDraft(personId: string, value: string) {
    setEmailDrafts((drafts) => ({ ...drafts, [personId]: value }))
    setEmailFailed((failed) => (failed[personId] ? { ...failed, [personId]: false } : failed))
  }

  async function saveEmails() {
    setSavingEmail(true)
    const nextFailed: Record<string, boolean> = {}
    await Promise.all(
      changedEmployees.map(async (e) => {
        const value = emailValue(e).trim()
        try {
          await companyApi.updateEmployee(e.personId, { email: value })
          // Успех: значение записано в карточку — обновляем строку у родителя и убираем
          // черновик, чтобы строка перестала считаться изменённой.
          onEmployeeChanged({ ...e, email: value })
          setEmailDrafts((drafts) => {
            const rest = { ...drafts }
            delete rest[e.personId]
            return rest
          })
        } catch {
          nextFailed[e.personId] = true
        }
      }),
    )
    setEmailFailed(nextFailed)
    setSavingEmail(false)
  }

  return (
    <Card
      header={
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <span>{individual ? 'Ваши документы' : 'Документы сотрудников'}</span>
          <span className="flex flex-wrap gap-x-4 text-13 font-normal text-txt-secondary">
            <span>{trainingConsentCounterLabel(consentsUploaded, employees.length)}</span>
            {needDocs.length > 0 ? (
              <span>{trainingEducationDocCounterLabel(docsUploaded, needDocs.length)}</span>
            ) : null}
          </span>
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="text-13 text-txt-secondary">
          Согласия{needDocs.length > 0 ? ' и документы об образовании' : ''} сохраняются{' '}
          {individual ? 'в вашей карточке' : 'в карточках сотрудников'} и подтягиваются в следующие заказы автоматически. Здесь можно заменить файл
          ({TRAINING_PERSON_DOC_FORMATS}) или скачать шаблон согласия.
        </p>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
          onClick={() => downloadFromUrl(trainingApi.consentTemplateUrl())}
        >
          Скачать шаблон согласия
        </Button>
      </div>

      {employees.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Сотрудники не выбраны"
          description="В заказе нет сотрудников — документы загружать не для кого"
        />
      ) : (
        <>
          <DataTable>
            {/* Ответ заказчицы 04.11, п.12.2–12.5: ФИО и «Подразделение / должность» — шире,
                «Согласие ПД» и «Документ о ВО/СПО» — по содержимому; заголовок — низкий. */}
            <DataTableHead>
              <DataTableRow className="[&>th]:py-1">
                <DataTableHeaderCell className="w-64">ФИО</DataTableHeaderCell>
                <DataTableHeaderCell className="w-72">Подразделение / должность</DataTableHeaderCell>
                <DataTableHeaderCell>Email</DataTableHeaderCell>
                <DataTableHeaderCell className="w-px whitespace-nowrap">Согласие ПД</DataTableHeaderCell>
                {needDocs.length > 0 ? (
                  <DataTableHeaderCell className="w-px whitespace-nowrap">
                    {EDUCATION_DOCUMENT_LABEL}
                  </DataTableHeaderCell>
                ) : null}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {employees.map((e) => {
                const value = emailValue(e)
                const invalid = value.trim() !== '' && !isValidEmail(value.trim())
                return (
                  <DataTableRow key={e.personId}>
                    <DataTableCell className="font-medium text-txt">{e.fullName}</DataTableCell>
                    <DataTableCell>
                      {e.departmentName}
                      {e.position ? ` · ${e.position}` : ''}
                    </DataTableCell>
                    <DataTableCell>
                      <div className="flex flex-col gap-1">
                        <Input
                          type="email"
                          className="w-56"
                          value={value}
                          invalid={invalid}
                          disabled={savingEmail}
                          placeholder="email@example.com"
                          aria-label={`Email — ${e.fullName}`}
                          onChange={(ev) => setEmailDraft(e.personId, ev.target.value)}
                        />
                        {emailFailed[e.personId] ? (
                          <span className="text-12 text-red">Не удалось сохранить email</span>
                        ) : null}
                      </div>
                    </DataTableCell>
                    <DataTableCell>
                      <ConsentCell employee={e} onChanged={onEmployeeChanged} />
                    </DataTableCell>
                    {needDocs.length > 0 ? (
                      <DataTableCell>
                        {e.needsEducationDocument ? (
                          <EducationDocumentCell employee={e} onChanged={onEmployeeChanged} />
                        ) : (
                          <span className="text-txt-tertiary" title="Не требуется по программам слушателя">
                            —
                          </span>
                        )}
                      </DataTableCell>
                    ) : null}
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>

          <div className="mt-3 flex justify-end">
            <Button disabled={!canSaveEmails} onClick={() => void saveEmails()}>
              {savingEmail ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
